/**
 * ChartPage — the Levey-Jennings card. A Recharts ComposedChart plots the chronological QC series with
 * the ±1/2/3 SD bands + the cible mean (ReferenceLines off the lot's FROZEN stats), and each point as a
 * custom dot colored by verdict (◆ critical = a violated point, ● normal = accepted) so a violation reads
 * by SHAPE + COLOR, never color alone. Below the chart, a table lists every point with its z + violated
 * rules. Filter by parametre [+ lot, niveau, automate]. Full 5-state DoD + module-absent degradation.
 *
 * Data: GET /qc/points (cap qc.point.read). The lot picker reads the validated lots (cap qc.lot.manage,
 * gracefully skipped if the user lacks it). Read-only screen — no mutations here.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  type DotProps,
} from "recharts";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  OfflineState,
  Select,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { QC_CAPS } from "../caps";
import { useChart, useLabId, useLots } from "../api";
import { NIVEAUX, type LjPoint, type Niveau } from "../types";
import { idStr } from "../types";
import { fmtNum, fmtTime, fmtZ, isModuleAbsent } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { VerdictBadge } from "../components/StatusBadge";
import { ModuleAbsentState } from "../components/ModuleAbsentState";

interface ChartDatum extends LjPoint {
  idx: number;
  violated: boolean;
}

/** A point dot whose shape+color carries the verdict (violation ◆ critical, else ● normal). */
function PointDot(props: DotProps & { payload?: ChartDatum }) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || !payload) return null;
  const violated = payload.violated;
  const fill = violated ? "var(--nv-critical)" : "var(--nv-normal)";
  return (
    <g aria-hidden>
      {violated ? (
        // a diamond (◆) — reads as a violation by shape, not color alone
        <path d={`M ${cx} ${cy - 6} L ${cx + 6} ${cy} L ${cx} ${cy + 6} L ${cx - 6} ${cy} Z`} fill={fill} stroke="var(--nv-surface)" strokeWidth={1.5} />
      ) : (
        <circle cx={cx} cy={cy} r={4} fill={fill} stroke="var(--nv-surface)" strokeWidth={1.5} />
      )}
    </g>
  );
}

export function ChartPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(QC_CAPS.pointRead);
  const canManageLots = useCan(QC_CAPS.lotManage);

  // The applied filter drives the query; the form fields are local until "Afficher".
  const [parametreInput, setParametreInput] = useState("");
  const [niveauInput, setNiveauInput] = useState<Niveau | "">("");
  const [automateInput, setAutomateInput] = useState("");
  const [lotInput, setLotInput] = useState("");
  const [applied, setApplied] = useState<{
    parametre_code: string;
    niveau: Niveau | "";
    automate_id: string;
    lot_controle_id: string;
  } | null>(null);

  const lotsQuery = useLots(labId, canRead && canManageLots && online);
  const validatedLots = useMemo(
    () => (lotsQuery.data ?? []).filter((l) => l.statut === "valide"),
    [lotsQuery.data],
  );

  const chartQuery = useChart(
    labId,
    {
      parametre_code: applied?.parametre_code ?? "",
      ...(applied?.lot_controle_id ? { lot_controle_id: applied.lot_controle_id } : {}),
      ...(applied?.niveau ? { niveau: applied.niveau } : {}),
      ...(applied?.automate_id ? { automate_id: applied.automate_id } : {}),
    },
    canRead && online && !!applied,
  );

  const data: ChartDatum[] = useMemo(
    () =>
      (chartQuery.data?.points ?? []).map((p, idx) => ({
        ...p,
        idx: idx + 1,
        violated: p.verdict === "rejete" || (p.regles_violees?.length ?? 0) > 0,
      })),
    [chartQuery.data],
  );

  const bands = chartQuery.data?.bands ?? null;

  const yDomain = useMemo<[number, number] | undefined>(() => {
    if (!data.length && !bands) return undefined;
    const vals: number[] = data.map((d) => d.valeur);
    if (bands) vals.push(bands.plus3, bands.minus3);
    if (!vals.length) return undefined;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min || 1) * 0.1;
    return [min - pad, max + pad];
  }, [data, bands]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (parametreInput.trim() === "") return;
    setApplied({
      parametre_code: parametreInput.trim(),
      niveau: niveauInput,
      automate_id: automateInput.trim(),
      lot_controle_id: lotInput.trim(),
    });
  };

  const columns: ReadonlyArray<Column<ChartDatum>> = [
    {
      key: "at",
      header: t("qc.chart.col.at"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{fmtTime(r.at)}</span>,
    },
    {
      key: "valeur",
      header: t("qc.chart.col.valeur"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{fmtNum(r.valeur)}</span>,
    },
    {
      key: "z",
      header: t("qc.chart.col.z"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{fmtZ(r.z)}</span>,
    },
    { key: "verdict", header: t("qc.chart.col.verdict"), cell: (r) => <VerdictBadge verdict={r.verdict} /> },
    {
      key: "rules",
      header: t("qc.chart.col.rules"),
      mono: true,
      cell: (r) =>
        r.regles_violees?.length ? (
          <span className="nv-ltr-island" style={{ color: "var(--nv-critical)" }}>
            {r.regles_violees.join(", ")}
          </span>
        ) : (
          t("qc.chart.rules.none")
        ),
    },
  ];

  const lotOptions = [
    { value: "", label: t("qc.chart.lot.all") },
    ...validatedLots.map((l) => ({ value: idStr(l.id), label: `${l.code} · ${l.parametre_code}` })),
  ];

  if (!canRead) return <ForbiddenState requiredCapabilities={[QC_CAPS.pointRead]} />;

  const header = <PageHeader title={t("qc.chart.title")} subtitle={t("qc.chart.subtitle")} />;

  const filterBar = (
    <Card>
      <form onSubmit={apply}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr)) auto",
            gap: "var(--nv-s-3)",
            alignItems: "end",
          }}
        >
          <FormField label={t("qc.parametre")}>
            {(p) => (
              <Input
                {...p}
                mono
                autoComplete="off"
                placeholder={t("qc.parametre.placeholder")}
                value={parametreInput}
                onChange={(e) => setParametreInput(e.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("qc.niveau")}>
            {(p) => (
              <Select
                {...p}
                value={niveauInput}
                onChange={(e) => setNiveauInput(e.target.value as Niveau | "")}
              >
                <option value="">{t("qc.automate.all")}</option>
                {NIVEAUX.map((n) => (
                  <option key={n} value={n}>
                    {t(`qc.niveau.${n}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("qc.chart.lot")}>
            {(p) => (
              <Select {...p} options={lotOptions} value={lotInput} onChange={(e) => setLotInput(e.target.value)} />
            )}
          </FormField>
          <FormField label={`${t("qc.automate")} (${t("qc.field.optional")})`}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="numeric"
                autoComplete="off"
                value={automateInput}
                onChange={(e) => setAutomateInput(e.target.value)}
              />
            )}
          </FormField>
          <Button type="submit" disabled={parametreInput.trim() === ""}>
            {t("qc.chart.filter.run")}
          </Button>
        </div>
      </form>
    </Card>
  );

  let result: React.ReactNode;
  if (!online) {
    result = <OfflineState onRetry={() => void chartQuery.refetch()} cached={!!chartQuery.data} />;
  } else if (!applied) {
    result = <EmptyState glyph="◷" title={t("qc.chart.prompt.title")} description={t("qc.chart.prompt.desc")} />;
  } else if (chartQuery.isLoading) {
    result = <LoadingState variant="table" rows={6} />;
  } else if (chartQuery.isError && isModuleAbsent(chartQuery.error)) {
    result = <ModuleAbsentState />;
  } else if (chartQuery.isError) {
    result = (
      <ErrorState
        message={chartQuery.error.message}
        code={chartQuery.error.code}
        onRetry={() => void chartQuery.refetch()}
      />
    );
  } else if (data.length === 0) {
    result = <EmptyState glyph="◌" title={t("qc.chart.empty.title")} description={t("qc.chart.empty.desc")} />;
  } else {
    result = (
      <>
        <Card title={t("qc.chart.title")}>
          {!bands && (
            <p style={{ color: "var(--nv-high)", margin: "0 0 var(--nv-s-3)", fontSize: "var(--nv-fs-caption)" }}>
              {t("qc.chart.nobands")}
            </p>
          )}
          <div style={{ width: "100%", height: 340 }} role="img" aria-label={t("qc.chart.title")}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 12, right: 20, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="var(--nv-line-faint)" vertical={false} />
                <XAxis
                  dataKey="idx"
                  stroke="var(--nv-slate)"
                  fontSize={12}
                  tickLine={false}
                  label={{ value: t("qc.chart.axis.point"), position: "insideBottom", offset: -4, fill: "var(--nv-slate)", fontSize: 11 }}
                />
                <YAxis
                  stroke="var(--nv-slate)"
                  fontSize={12}
                  width={48}
                  {...(yDomain ? { domain: yDomain } : {})}
                  tickFormatter={(v: number) => fmtNum(v)}
                />
                <Tooltip
                  cursor={{ stroke: "var(--nv-line)" }}
                  contentStyle={{
                    background: "var(--nv-surface)",
                    border: "1px solid var(--nv-line)",
                    borderRadius: "var(--nv-r-sm)",
                    color: "var(--nv-ink)",
                  }}
                  formatter={(value: number, _name, item) => {
                    const p = item?.payload as ChartDatum | undefined;
                    return [
                      `${fmtNum(value)}  (z ${fmtZ(p?.z)})${p?.regles_violees?.length ? ` · ${p.regles_violees.join(", ")}` : ""}`,
                      t("qc.chart.tip.value"),
                    ];
                  }}
                  labelFormatter={(idx) => {
                    const p = data[Number(idx) - 1];
                    return p ? fmtTime(p.at) : String(idx);
                  }}
                />
                {bands && (
                  <>
                    <ReferenceLine y={bands.plus3} stroke="var(--nv-critical)" strokeDasharray="2 4" />
                    <ReferenceLine y={bands.minus3} stroke="var(--nv-critical)" strokeDasharray="2 4" />
                    <ReferenceLine y={bands.plus2} stroke="var(--nv-high)" strokeDasharray="4 4" />
                    <ReferenceLine y={bands.minus2} stroke="var(--nv-high)" strokeDasharray="4 4" />
                    <ReferenceLine y={bands.plus1} stroke="var(--nv-line)" strokeDasharray="6 4" />
                    <ReferenceLine y={bands.minus1} stroke="var(--nv-line)" strokeDasharray="6 4" />
                    <ReferenceLine y={bands.cible} stroke="var(--nv-azure)" strokeWidth={1.5} />
                  </>
                )}
                <Line
                  type="linear"
                  dataKey="valeur"
                  stroke="var(--nv-azure)"
                  strokeWidth={1.5}
                  isAnimationActive={false}
                  dot={<PointDot />}
                  activeDot={{ r: 6, fill: "var(--nv-azure)" }}
                />
                {/* invisible scatter to keep the tooltip rich at every x even if Line dot is custom */}
                <Scatter dataKey="valeur" fill="transparent" isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <ChartLegend />
        </Card>

        <Card padded={false}>
          <div style={{ padding: "var(--nv-s-3)" }}>
            <h2 style={{ fontSize: "var(--nv-fs-title)", margin: "0 0 var(--nv-s-3)" }}>{t("qc.chart.points.title")}</h2>
            <Table
              columns={columns}
              rows={data}
              rowKey={(r) => r.id}
              caption={t("qc.chart.points.title")}
              pageSize={20}
            />
          </div>
        </Card>
      </>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}
      {filterBar}
      {result}
    </div>
  );
}

/** Legend pairing each band with its shape/dash so the chart reads without relying on color alone. */
function ChartLegend() {
  const { t } = useT();
  const item = (swatch: React.ReactNode, label: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nv-s-2)", fontSize: "var(--nv-fs-caption)", color: "var(--nv-slate)" }}>
      {swatch}
      {label}
    </span>
  );
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-4)", marginTop: "var(--nv-s-3)" }}>
      {item(<span aria-hidden style={{ width: 18, height: 0, borderTop: "2px solid var(--nv-azure)" }} />, t("qc.chart.legend.cible"))}
      {item(<span aria-hidden style={{ width: 18, height: 0, borderTop: "2px dashed var(--nv-line)" }} />, t("qc.chart.legend.sd1"))}
      {item(<span aria-hidden style={{ width: 18, height: 0, borderTop: "2px dashed var(--nv-high)" }} />, t("qc.chart.legend.sd2"))}
      {item(<span aria-hidden style={{ width: 18, height: 0, borderTop: "2px dashed var(--nv-critical)" }} />, t("qc.chart.legend.sd3"))}
      {item(<span aria-hidden style={{ color: "var(--nv-normal)" }}>●</span>, t("qc.chart.legend.point"))}
      {item(<span aria-hidden style={{ color: "var(--nv-critical)" }}>◆</span>, t("qc.chart.legend.violation"))}
    </div>
  );
}
